import React from 'react';
import styled from 'styled-components';

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f0f7ff 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;
  overflow: hidden;

  /* Animated background elements */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    animation: float 8s ease-in-out infinite;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -20%;
    left: -5%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    animation: float 10s ease-in-out infinite reverse;
    pointer-events: none;
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(30px);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: 80vh;

    &::before {
      width: 300px;
      height: 300px;
      right: -20%;
    }

    &::after {
      width: 250px;
      height: 250px;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem;
    min-height: 60vh;

    &::before,
    &::after {
      display: none;
    }
  }
`;

const ContentWrapper = styled.div`
  text-align: center;
  max-width: 650px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const IconWrapper = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    width: 130px;
    height: 130px;
  }

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
  }
`;

const IconBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* Animated gradient border */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(59, 130, 246, 0.2),
      transparent
    );
    animation: shimmerWide 3s infinite;
  }

  @keyframes shimmerWide {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const IconContent = styled.div`
  font-size: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 4px 12px rgba(59, 130, 246, 0.2));

  @media (max-width: 768px) {
    font-size: 3.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 480px) {
    gap: 0.375rem;
  }
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1976d2;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  @media (max-width: 480px) {
    padding: 0.375rem 0.875rem;
    font-size: 0.7rem;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 800;
  color: #2c3e50;
  line-height: 1.2;
  letter-spacing: -0.5px;

  /* Gradient text option */
  background: linear-gradient(135deg, #2c3e50 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.125rem;
  color: #666666;
  line-height: 1.7;
  max-width: 500px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.6;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.5rem;
  width: 100%;
  margin: 1rem 0;

  @media (max-width: 768px) {
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FeatureItem = styled.div`
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(59, 130, 246, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(59, 130, 246, 0.3);
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(59, 130, 246, 0.12);
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.75rem;
  display: inline-block;
`;

const FeatureText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #2c3e50;
  line-height: 1.4;
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;

  @media (max-width: 480px) {
    gap: 0.75rem;
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.35);
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(59, 130, 246, 0.45);
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    padding: 0.875rem 1.5rem;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: white;
  color: #3b82f6;
  border: 2px solid #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);

  &:hover {
    background: #f0f7ff;
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.25);
  }

  &:active {
    background: #e3f2fd;
  }

  &::before {
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.2), transparent);
  }
`;

const IllustrationContainer = styled.div`
  width: 100%;
  max-width: 450px;
  margin: 1rem 0;
  position: relative;

  svg {
    width: 100%;
    height: auto;
    filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.1));
  }

  @media (max-width: 768px) {
    max-width: 350px;
  }

  @media (max-width: 480px) {
    max-width: 250px;
    margin: 0.75rem 0;
  }
`;

const Divider = styled.div`
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #3b82f6, transparent);
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 40px;
    height: 2px;
  }
`;

const HelpText = styled.p`
  margin: 1.5rem 0 0 0;
  font-size: 0.875rem;
  color: #999999;
  line-height: 1.6;

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    margin-top: 1rem;
  }
`;


const DataEmptyIcon = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
    <path
      d="M20 15H60C62.21 15 64 16.79 64 19V61C64 63.21 62.21 65 60 65H20C17.79 65 16 63.21 16 61V19C16 16.79 17.79 15 20 15ZM20 19V61H60V19H20Z"
      fill="#3b82f6"
    />
    <path
      d="M24 28H56C57.1 28 58 27.1 58 26C58 24.9 57.1 24 56 24H24C22.9 24 22 24.9 22 26C22 27.1 22.9 28 24 28Z"
      fill="#3b82f6"
    />
    <path
      d="M24 38H56C57.1 38 58 37.1 58 36C58 34.9 57.1 34 56 34H24C22.9 34 22 34.9 22 36C22 37.1 22.9 38 24 38Z"
      fill="#3b82f6"
    />
    <path
      d="M24 48H56C57.1 48 58 47.1 58 46C58 44.9 57.1 44 56 44H24C22.9 44 22 44.9 22 46C22 47.1 22.9 48 24 48Z"
      fill="#3b82f6"
    />
  </svg>
);

const SearchEmptyIcon = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
    <circle cx="35" cy="35" r="20" fill="none" stroke="#3b82f6" strokeWidth="3" />
    <path
      d="M50 50L60 60"
      stroke="#3b82f6"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M25 35H45M35 25V45"
      stroke="#3b82f6"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.5"
    />
  </svg>
);

const DefaultEmptyIcon = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
    <rect width="80" height="80" rx="20" fill="url(#paint0_linear)" opacity="0.1" />
    <circle cx="40" cy="40" r="25" fill="none" stroke="#3b82f6" strokeWidth="2.5" />
    <path
      d="M30 40H50M40 30V50"
      stroke="#3b82f6"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient id="paint0_linear" x1="0" y1="0" x2="80" y2="80">
        <stop offset="0%" stopColor="#3b82f6" />
        <stop offset="100%" stopColor="#2563eb" />
      </linearGradient>
    </defs>
  </svg>
);


const EmptyStatePage = ({
  // Content
  title = 'No Data to Display',
  subtitle = 'It looks like there\'s nothing here yet. Get started by creating your first item or importing existing data.',
  statusBadge = 'EMPTY',
  
  // Visual
  icon = 'default', // 'default', 'data', 'search', or custom JSX/emoji
  illustration = null,
  showIcon = true,
  features = null, // Array of {icon, text}
  
  // Actions
  primaryAction = null,
  secondaryAction = null,
  showDivider = true,
  helpText = null,
}) => {
  const renderIcon = () => {
    if (!showIcon) return null;

    if (React.isValidElement(icon)) {
      return icon;
    }

    switch (icon) {
      case 'data':
        return <DataEmptyIcon />;
      case 'search':
        return <SearchEmptyIcon />;
      case 'default':
      default:
        return <DefaultEmptyIcon />;
    }
  };

  // Default features if not provided
  const defaultFeatures = [

  ];

  const featuresToShow = features || defaultFeatures;

  return (
    <EmptyStateContainer>
      <ContentWrapper>
        {/* Illustration or Icon */}
        {illustration ? (
          <IllustrationContainer>{illustration}</IllustrationContainer>
        ) : (
          <IconWrapper>
            <IconBackground>{renderIcon()}</IconBackground>
          </IconWrapper>
        )}

        {/* Divider */}
        {showDivider && <Divider />}

        {/* Status Badge */}
        {statusBadge && <StatusBadge>{statusBadge}</StatusBadge>}

        {/* Title Section */}
        <TitleSection>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
        </TitleSection>

        {/* Features Grid */}
        {featuresToShow && featuresToShow.length > 0 && (
          <FeaturesGrid>
            {featuresToShow.map((feature, index) => (
              <FeatureItem key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureText>{feature.text}</FeatureText>
              </FeatureItem>
            ))}
          </FeaturesGrid>
        )}

        {/* Action Buttons */}
        {(primaryAction || secondaryAction) && (
          <ActionContainer>
            {primaryAction && (
              <PrimaryButton
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
              >
                {primaryAction.icon && <span>{primaryAction.icon}</span>}
                {primaryAction.label}
              </PrimaryButton>
            )}
            {secondaryAction && (
              <SecondaryButton
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled}
              >
                {secondaryAction.icon && <span>{secondaryAction.icon}</span>}
                {secondaryAction.label}
              </SecondaryButton>
            )}
          </ActionContainer>
        )}

        {/* Help Text */}
        {helpText && <HelpText>{helpText}</HelpText>}
      </ContentWrapper>
    </EmptyStateContainer>
  );
};

export default EmptyStatePage;